import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Card, DataTable } from '@w7/ui'
import { bookmakerApi, bmErrorMessage, unwrapBmList } from '@/api/bookmaker'
import { BmPage } from './BmPage'
import {
  marketListEditId,
  marketListEventName,
  marketListGame,
  marketListMarketId,
  marketListName,
  marketListRowKey,
  marketListStatus,
} from './marketListDisplay'

export function MarketListPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<Record<string, unknown>[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [pageNo, setPageNo] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const load = useCallback(async (page: number) => {
    setBusy(true)
    setError(null)
    try {
      const data = await bookmakerApi.marketList({ pageNo: page })
      const list = unwrapBmList(data)
      setRows(list)
      if (data && typeof data === 'object') {
        const o = data as Record<string, unknown>
        if (typeof o.totalItems === 'number') setTotalItems(o.totalItems)
        if (typeof o.itemsPerPage === 'number') setItemsPerPage(o.itemsPerPage)
      }
      setPageNo(page)
    } catch (e) {
      setError(bmErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }, [])

  useEffect(() => {
    void load(1)
  }, [load])

  const totalPages = Math.max(1, Math.ceil((totalItems || rows.length) / (itemsPerPage || 10)))

  return (
    <BmPage
      title="Markets"
      description="Lab Market list — same columns as In Play"
      error={error}
      actions={
        <>
          <Button onClick={() => void load(pageNo)} disabled={busy}>
            Refresh
          </Button>
          <Button onClick={() => navigate('/createmarket')}>Create</Button>
        </>
      }
    >
      <Card>
        <DataTable
          label="Markets"
          columns={[
            {
              id: 'sr',
              header: 'Sr. No',
              cell: (_r, i) => String(itemsPerPage * (pageNo - 1) + (i ?? 0) + 1),
            },
            {
              id: 'marketId',
              header: 'Market ID',
              cell: (r: Record<string, unknown>) => marketListMarketId(r),
            },
            {
              id: 'name',
              header: 'Name',
              cell: (r: Record<string, unknown>) => marketListName(r),
            },
            {
              id: 'event',
              header: 'Event Name',
              cell: (r: Record<string, unknown>) => marketListEventName(r),
            },
            {
              id: 'game',
              header: 'Game',
              cell: (r: Record<string, unknown>) => marketListGame(r),
            },
            {
              id: 'status',
              header: 'Status',
              cell: (r: Record<string, unknown>) => marketListStatus(r),
            },
            {
              id: 'action',
              header: 'Action',
              cell: (r: Record<string, unknown>) => {
                const mid = marketListEditId(r)
                return (
                  <Button
                    variant="secondary"
                    disabled={!mid}
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate('/editmarket/' + mid)
                    }}
                  >
                    Edit
                  </Button>
                )
              },
            },
          ]}
          rows={rows}
          loading={busy}
          rowKey={(r) => marketListRowKey(r) || String(Math.random())}
          onRowClick={(r) => {
            const id = marketListEditId(r)
            if (id) navigate('/editmarket/' + id)
          }}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
          <Button
            variant="secondary"
            disabled={busy || pageNo <= 1}
            onClick={() => void load(pageNo - 1)}
          >
            Prev
          </Button>
          <span>
            Page {pageNo} / {totalPages}
          </span>
          <Button
            variant="secondary"
            disabled={busy || pageNo >= totalPages}
            onClick={() => void load(pageNo + 1)}
          >
            Next
          </Button>
        </div>
      </Card>
    </BmPage>
  )
}
